/*
 * Vestige interactions
 *
 * Header state over the hero, hero parallax, swipeable product-card galleries,
 * horizontal product-page gallery indicators, and the product-page info drawers.
 * The tessellated intro overlay runs from its own inline script so it can act
 * before first paint; see snippets/vestige-intro.liquid.
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  /* ------------------------------------------------------------------------
   * Header: transparent over the hero, solid once the hero has scrolled past
   * ---------------------------------------------------------------------- */

  var headerObserver = null;
  var stickyObserver = null;

  // Kept in step with the inline measurement in layout/theme.liquid: fractional,
  // and taken from the last section's bottom edge so rounding cannot leave a
  // hairline of page background showing above the hero.
  function measureHeaderGroup() {
    var sections = document.querySelectorAll('.shopify-section-group-header-group');
    if (!sections.length) return 0;

    // offsetTop rather than a viewport-relative bottom: this also runs on resize,
    // when the header may be stuck to the top and its on-screen position would no
    // longer match its position in the document.
    var last = sections[sections.length - 1];
    var height = last.offsetTop + last.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--vst-header-group-h', height + 'px');
    return height;
  }

  function initHeaderOverlay() {
    var root = document.documentElement;
    if (headerObserver) {
      headerObserver.disconnect();
      headerObserver = null;
    }
    if (stickyObserver) {
      stickyObserver.disconnect();
      stickyObserver = null;
    }

    var hero = document.querySelector('.vst-hero-overlay');
    if (!hero) {
      root.classList.remove('vst-header-overlay', 'vst-header-solid');
      return;
    }

    root.classList.add('vst-header-overlay');
    measureHeaderGroup();

    var headerSection = document.querySelector('.section-header');
    var headerHeight = headerSection ? headerSection.offsetHeight : 0;

    if (!('IntersectionObserver' in window)) {
      root.classList.add('vst-header-solid');
      return;
    }

    // Solid once the hero's bottom edge passes under the header, and whenever Dawn's sticky header has
    // come back down on scroll-up: it then sits over whatever is on screen (the hero photograph on a
    // phone), so it must not stay transparent with a white logo.
    var heroUnderHeader = true;
    function apply() {
      // Dawn marks the header 'scrolled-past-header' once the page has scrolled beyond it (any sticky mode)
      // and clears it back at the top.
      var sticky = !!headerSection && headerSection.classList.contains('scrolled-past-header');
      root.classList.toggle('vst-header-solid', !heroUnderHeader || sticky);
    }
    headerObserver = new IntersectionObserver(
      function (entries) {
        heroUnderHeader = entries[entries.length - 1].isIntersecting;
        apply();
      },
      { rootMargin: '-' + Math.round(headerHeight) + 'px 0px 0px 0px', threshold: 0 }
    );
    headerObserver.observe(hero);
    if (headerSection && 'MutationObserver' in window) {
      stickyObserver = new MutationObserver(apply);
      stickyObserver.observe(headerSection, { attributes: true, attributeFilter: ['class'] });
    }
    apply();
  }

  /* ------------------------------------------------------------------------
   * Hero parallax
   *
   * The image is 130% of its container's height, so it has 15% of headroom in
   * each direction to travel through as the hero scrolls past. Driven by
   * transform rather than background-attachment: fixed, which janks badly and
   * behaves inconsistently on mobile Safari.
   * ---------------------------------------------------------------------- */

  function initParallax() {
    var nodes = Array.prototype.slice.call(document.querySelectorAll('.vst-parallax'));
    if (!nodes.length) return;

    var ticking = false;

    function apply() {
      ticking = false;
      if (reducedMotion.matches) return;

      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        var image = node.querySelector('img, svg');
        if (!image) continue;

        var rect = node.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight || !rect.height) continue;

        var progress = Math.min(Math.max(-rect.top / rect.height, 0), 1);
        var offset = (0.15 - 0.3 * progress) * rect.height;
        image.style.transform = 'translate3d(0, ' + offset.toFixed(2) + 'px, 0)';
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', apply);
    apply();
  }

  /* ------------------------------------------------------------------------
   * Product card gallery
   * ---------------------------------------------------------------------- */

  var SWIPE_THRESHOLD = 40;

  if (window.customElements && !customElements.get('vestige-card-gallery')) {
    customElements.define(
      'vestige-card-gallery',
      class extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;

          var scope = this.closest('.card__inner') || this.parentElement;
          this.track = scope ? scope.querySelector('.vst-cardgal__track') : null;
          if (!this.track) return;

          this.slides = this.track.children;
          this.count = this.slides.length;
          if (this.count < 2) return;

          this.index = 0;
          this.suppressClick = false;
          this.prevButton = this.querySelector('.vst-cardgal__nav--prev');
          this.nextButton = this.querySelector('.vst-cardgal__nav--next');
          this.dots = this.querySelectorAll('.vst-cardgal__dot');

          if (this.prevButton) this.prevButton.addEventListener('click', this.onNavClick.bind(this, -1));
          if (this.nextButton) this.nextButton.addEventListener('click', this.onNavClick.bind(this, 1));

          this.bindSwipe(this.closest('.card-wrapper') || scope);
          this.update();
          this.ready = true;
        }

        onNavClick(direction, event) {
          // The whole card is a product link; the arrows must not follow it.
          event.preventDefault();
          event.stopPropagation();
          this.go(direction);
        }

        go(direction) {
          var next = this.index + direction;
          if (next < 0) next = this.count - 1;
          if (next > this.count - 1) next = 0;
          this.index = next;
          this.update();
        }

        update() {
          this.track.style.transform = 'translate3d(-' + this.index * 100 + '%, 0, 0)';

          for (var i = 0; i < this.count; i++) {
            // Only the visible slide should be reachable by keyboard or a11y tree.
            this.slides[i].setAttribute('aria-hidden', i === this.index ? 'false' : 'true');
          }

          for (var d = 0; d < this.dots.length; d++) {
            this.dots[d].classList.toggle('vst-cardgal__dot--active', d === this.index);
          }
        }

        bindSwipe(host) {
          if (!host) return;

          var self = this;
          var startX = 0;
          var startY = 0;
          var deltaX = 0;
          var deltaY = 0;
          var tracking = false;

          host.addEventListener(
            'touchstart',
            function (event) {
              if (event.touches.length !== 1) return;
              tracking = true;
              deltaX = 0;
              deltaY = 0;
              startX = event.touches[0].clientX;
              startY = event.touches[0].clientY;
            },
            { passive: true }
          );

          host.addEventListener(
            'touchmove',
            function (event) {
              if (!tracking) return;
              deltaX = event.touches[0].clientX - startX;
              deltaY = event.touches[0].clientY - startY;
            },
            { passive: true }
          );

          host.addEventListener('touchend', function () {
            if (!tracking) return;
            tracking = false;

            // Ignore anything that reads as a vertical scroll rather than a swipe.
            if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return;

            self.suppressClick = true;
            setTimeout(function () {
              self.suppressClick = false;
            }, 400);

            self.go(deltaX < 0 ? 1 : -1);
          });

          // A swipe that ends over the card link would otherwise navigate away.
          host.addEventListener(
            'click',
            function (event) {
              if (!self.suppressClick) return;
              event.preventDefault();
              event.stopPropagation();
            },
            true
          );
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
   * Product page gallery indicators
   *
   * Dawn's media-gallery already gives us a horizontal scroll-snap slider with
   * working prev/next buttons; this adds dot indicators and keeps the slider in
   * step with variant image changes, which Dawn only handles when thumbnails
   * are rendered.
   * ---------------------------------------------------------------------- */

  if (window.customElements && !customElements.get('vestige-gallery-dots')) {
    customElements.define(
      'vestige-gallery-dots',
      class extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;

          this.gallery = this.closest('media-gallery');
          this.slider = this.gallery ? this.gallery.querySelector('[id^="Slider-Gallery"]') : null;
          if (!this.slider) return;

          this.render();
          if (!this.dots || this.dots.length < 2) return;

          this.onScroll = this.onScroll.bind(this);
          this.slider.addEventListener('scroll', this.onScroll, { passive: true });

          this.observer = new MutationObserver(this.onMutate.bind(this));
          this.observer.observe(this.slider, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class'],
          });

          this.ready = true;
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
          if (this.slider) this.slider.removeEventListener('scroll', this.onScroll);
        }

        render() {
          // The slides only: the zoom button inside each photo also carries data-media-id,
          // which doubled the dots. Same selector Dawn's SliderComponent counts for "1/4".
          this.slides = Array.prototype.slice.call(this.slider.querySelectorAll('[id^="Slide-"]'));
          this.innerHTML = '';

          if (this.slides.length < 2) {
            this.dots = [];
            this.hidden = true;
            return;
          }

          this.hidden = false;
          var list = document.createElement('div');
          list.className = 'vst-gallery__dots';

          for (var i = 0; i < this.slides.length; i++) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'vst-gallery__dot';
            dot.setAttribute('aria-label', 'Image ' + (i + 1) + ' of ' + this.slides.length);
            dot.addEventListener('click', this.goTo.bind(this, i));
            list.appendChild(dot);
          }

          this.appendChild(list);
          this.dots = list.children;
          this.setActive(0);
        }

        goTo(index) {
          if (!this.slides[index]) return;
          this.slider.scrollTo({ left: this.slides[index].offsetLeft, behavior: 'smooth' });
        }

        onScroll() {
          if (this.scrollTick) return;
          this.scrollTick = true;

          requestAnimationFrame(
            function () {
              this.scrollTick = false;

              var middle = this.slider.scrollLeft + this.slider.clientWidth / 2;
              var closest = 0;
              var shortest = Infinity;

              for (var i = 0; i < this.slides.length; i++) {
                var centre = this.slides[i].offsetLeft + this.slides[i].clientWidth / 2;
                var distance = Math.abs(centre - middle);
                if (distance < shortest) {
                  shortest = distance;
                  closest = i;
                }
              }

              this.setActive(closest);
            }.bind(this)
          );
        }

        onMutate(records) {
          for (var i = 0; i < records.length; i++) {
            var record = records[i];

            // Slides added or removed by a variant change: rebuild the dots.
            if (record.type === 'childList' && (record.addedNodes.length || record.removedNodes.length)) {
              this.render();
              return;
            }

            // A variant selection marked a different slide active: scroll to it.
            var target = record.target;
            if (
              record.type === 'attributes' &&
              target.classList &&
              target.classList.contains('is-active') &&
              target.hasAttribute('data-media-id')
            ) {
              this.slider.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
              return;
            }
          }
        }

        setActive(index) {
          if (!this.dots) return;
          for (var i = 0; i < this.dots.length; i++) {
            this.dots[i].classList.toggle('vst-gallery__dot--active', i === index);
            this.dots[i].setAttribute('aria-current', i === index ? 'true' : 'false');
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
   * Product page drawers
   * ---------------------------------------------------------------------- */

  function initDrawers() {
    if (document.vestigeDrawersBound) return;
    document.vestigeDrawersBound = true;

    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-vst-drawer-open]');
      if (opener) {
        var drawer = document.getElementById(opener.getAttribute('data-vst-drawer-open'));
        if (drawer && typeof drawer.showModal === 'function') {
          event.preventDefault();
          drawer.showModal();
        }
        return;
      }

      var closer = event.target.closest('[data-vst-drawer-close]');
      if (closer) {
        var open = closer.closest('dialog');
        if (open) open.close();
        return;
      }

      // Clicking the backdrop resolves to the dialog element itself.
      if (event.target.matches && event.target.matches('dialog.vst-drawer')) {
        event.target.close();
      }
    });
  }

  /* ------------------------------------------------------------------------
   * Page transition
   *
   * Clicking an internal link closes the hexagon veil over the current page
   * before the browser navigates; the next page opens it again on load. The
   * veil itself lives in snippets/vestige-intro.liquid, which exposes
   * window.VestigeIntro.
   * ---------------------------------------------------------------------- */

  // Links these own are handled by Dawn's own JS (AJAX filtering, in-page
  // panels) or are not navigations at all, so they must pass straight through.
  var TRANSITION_EXCLUDE = [
    '.js-facet-remove',
    'facet-filters-form',
    '.mobile-facets',
    '.skip-to-content-link',
    'terrain-studio',
    '[data-vst-no-transition]',
  ].join(',');

  function isTransitionableLink(link) {
    if (!link || !link.href) return false;
    if (link.hasAttribute('download')) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.getAttribute('href').charAt(0) === '#') return false;
    if (link.closest(TRANSITION_EXCLUDE)) return false;

    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return false;
    }

    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    if (url.origin !== window.location.origin) return false;

    // A link to the current page with only a fragment change is not a navigation.
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) {
      return false;
    }

    return true;
  }

  function initPageTransition() {
    if (document.vestigeTransitionBound) return;
    if (window.Shopify && window.Shopify.designMode) return;

    var intro = window.VestigeIntro;
    if (!intro || intro.reduced || typeof intro.playIn !== 'function') return;

    document.vestigeTransitionBound = true;
    var navigating = false;

    document.addEventListener('click', function (event) {
      if (navigating) return;
      // Let the browser handle modified clicks: new tab, download, middle button.
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var link = event.target.closest('a[href]');
      if (!isTransitionableLink(link)) return;

      event.preventDefault();
      navigating = true;

      var destination = link.href;
      intro.playIn(function () {
        window.location.href = destination;
      });

      // If the navigation is blocked or simply never happens, don't strand the
      // visitor behind the veil.
      setTimeout(function () {
        navigating = false;
        if (intro.playOut) intro.playOut();
      }, 4000);
    });
  }

  /* ------------------------------------------------------------------------
   * Boot
   * ---------------------------------------------------------------------- */

  function init() {
    initHeaderOverlay();
    initParallax();
    initDrawers();
    initPageTransition();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener(
    'resize',
    debounce(function () {
      measureHeaderGroup();
      initHeaderOverlay();
    }, 200)
  );

  // Theme editor: sections are swapped in and out without a page load.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', initHeaderOverlay);
})();
